import { ArrowRight, Camera, Clock, Heart, Pill, ShieldCheck } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";

const BOOKING_URL =
  "https://www.timetopet.com/portal/tallylassiepets/create-account";

const visitOptions = [
  {
    name: "Express Care Visit",
    duration: "15 minutes",
    price: "Starts at $30",
    description:
      "A quick daytime let-out or home check for pets who need a shorter visit.",
  },
  {
    name: "Pet Pal Visit",
    duration: "30 minutes",
    price: "Starts at $35",
    description:
      "Combine a walk, feeding, a short let-out, or a home check in one popular visit.",
    href: "/pet-pal-visits",
  },
  {
    name: "Longer Love Visit",
    duration: "45 minutes",
    price: "Starts at $45",
    description:
      "Extra time for multiple pets, longer walks, feeding, or pets who need more attention.",
  },
  {
    name: "Bed & Breakfast",
    duration: "2-hour evening stay + 1-hour morning visit",
    price: "Starts at $150",
    description:
      "Evening and morning care to help keep your pet's routine consistent while you're away.",
  },
];

const careDetails = [
  {
    icon: Heart,
    title: "A routine that feels familiar",
    description:
      "Visits can include walks, feeding, short let-outs, and home checks based on your pet's needs.",
  },
  {
    icon: Camera,
    title: "Stay in the loop",
    description:
      "Pet Pal and Longer Love visits include photos and comprehensive visit reports.",
  },
  {
    icon: Pill,
    title: "Medication administration",
    description:
      "Medication can be provided during visits at no additional charge.",
  },
];

export default function PetSitting() {
  return (
    <div>
      <SEO
        title="Pet Sitting Services | Tallahassee Lassie"
        description="Explore Tallahassee Lassie's in-home pet sitting visits, from quick daytime check-ins to longer visits and Bed & Breakfast overnight care."
        path="/pet-sitting"
      />

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-2 md:gap-14">
            <Reveal>
              <div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
                  <ShieldCheck size={17} />
                  Professional in-home care
                </div>
                <h1 className="mb-6 font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl">
                  Pet Sitting in Tallahassee
                </h1>
                <p className="mb-8 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                  From a quick daytime check-in to an evening stay and morning
                  visit, choose in-home care that helps keep your pet&apos;s
                  routine familiar.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="h-14 px-7">
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                      Create an Account
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="h-14 border-primary px-7 text-primary hover:bg-primary hover:text-white"
                  >
                    <Link href="/policies-rates">View policies &amp; rates</Link>
                  </Button>
                </div>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <img
                src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/2756e354-7f76-46b4-8e00-09e0e79b46bb/IMG_3120.jpg"
                alt="A dog resting at home during an overnight Bed & Breakfast visit"
                className="aspect-[4/3] w-full object-cover shadow-xl"
                style={{ borderRadius: "32px 0 32px 0" }}
                fetchPriority="high"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <h2 className="mb-4 font-serif text-3xl font-bold text-foreground md:text-4xl">
                Choose the visit that fits
              </h2>
              <p className="text-lg leading-relaxed text-muted-foreground">
                Visit lengths and starting prices are listed below. Your care
                plan can be discussed when you request a Meet &amp; Greet.
              </p>
            </div>
          </Reveal>

          <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2">
            {visitOptions.map((option, index) => (
              <Reveal key={option.name} delay={index * 60}>
                <article
                  className="flex h-full flex-col border border-border/60 bg-card p-7"
                  style={{ borderRadius: "20px 0 20px 0" }}
                >
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <h3 className="font-serif text-2xl font-bold text-foreground">
                      {option.name}
                    </h3>
                    <span className="font-semibold text-primary">
                      {option.price}
                    </span>
                  </div>
                  <p className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Clock size={16} className="text-primary" />
                    {option.duration}
                  </p>
                  <p className="mb-6 flex-1 leading-relaxed text-foreground/75">
                    {option.description}
                  </p>
                  {option.href && (
                    <Link
                      href={option.href}
                      className="inline-flex min-h-11 items-center font-semibold text-primary hover:text-primary/80"
                    >
                      Learn about Pet Pal Visits
                      <ArrowRight size={16} className="ml-2" />
                    </Link>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <h2 className="mb-4 font-serif text-3xl font-bold text-foreground md:text-4xl">
                Thoughtful details for pet parents
              </h2>
            </div>
          </Reveal>
          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
            {careDetails.map((detail, index) => (
              <Reveal key={detail.title} delay={index * 70}>
                <article
                  className="h-full border border-border/60 bg-card p-7"
                  style={{ borderRadius: "20px 0 20px 0" }}
                >
                  <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10 text-secondary">
                    <detail.icon size={22} />
                  </span>
                  <h3 className="mb-2 font-serif text-xl font-bold text-foreground">
                    {detail.title}
                  </h3>
                  <p className="leading-relaxed text-foreground/75">
                    {detail.description}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4">
          <Reveal>
            <div
              className="mx-auto max-w-4xl bg-primary p-8 text-center text-primary-foreground md:p-12"
              style={{ borderRadius: "32px 0 32px 0" }}
            >
              <h2 className="mb-4 font-serif text-3xl font-bold md:text-4xl">
                Let&apos;s find the right care for your pet
              </h2>
              <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-primary-foreground/85">
                Create an account to request a Meet &amp; Greet and talk through
                your pet&apos;s routine with our team.
              </p>
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  variant="secondary"
                  className="h-14 bg-white px-7 text-primary hover:bg-white/90"
                >
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                    Book a Meet &amp; Greet
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-14 border-white px-7 text-white hover:bg-white/10"
                >
                  <Link href="/menu">See all services</Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
